// src/admin/eventlist.jsx
// イベント一覧（管理者）。JWT付きのAPIを呼ぶ（axios のインターセプターで Authorization が付く）。
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import "./eventlist.css";

const API_URL = import.meta.env.VITE_API_URL;

/* ========================================
 * ヘルパー
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, "0");

const formatDateJP = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} 開催`;
};

const APPLY_STATUS = {
  before: { label: "受付前", className: "isBefore" },
  accepting: { label: "受付中", className: "isAccepting" },
  ended: { label: "受付終了", className: "isEnded" },
};

const getApplyStatusDisplay = (status) =>
  APPLY_STATUS[status] || APPLY_STATUS.accepting;

// 今日の日付を "YYYY-MM-DD" で返す（ローカル日付）。開催日が今日以降なら「開催予定」、前なら「終了」
const todayStr = () => {
  const t = new Date();
  return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
};

// 絞り込みのプルダウンの選択肢（開催＝開催日が今日以降か前か / 公開＝公開サイトでの状態）
const DATE_FILTERS = [
  { key: "all", label: "すべて" },
  { key: "upcoming", label: "開催予定" },
  { key: "finished", label: "終了" },
];
const PUBLIC_FILTERS = [
  { key: "all", label: "すべて" },
  { key: "public", label: "公開中" },
  { key: "scheduled", label: "公開予定" },
  { key: "private", label: "非公開" },
];

// 検索用に、全角/半角・大文字/小文字・前後の空白をそろえる
const normalize = (s) => String(s || "").normalize("NFKC").toLowerCase().trim();

// 公開サイトでの状態（public: 公開中 / scheduled: ONだが申込終了まで非表示 / private: 非公開）
const PUBLIC_STATUS = {
  public: { label: "公開中", className: "isPublic" },
  scheduled: { label: "公開予定", sub: "申込終了後に公開", className: "isScheduled" },
  private: { label: "非公開", className: "isPrivate" },
};

/* ========================================
 * コンポーネント
 * ======================================== */
export default function AdminEventList() {
  const queryClient = useQueryClient();
  const listKey = ["events-list", "member"];

  // 公開スイッチの切り替え（成功したら一覧を取得し直す）
  const togglePublic = useMutation({
    mutationFn: async ({ eventId, isPublic }) => {
      const res = await axios.put(
        `${API_URL}/api/admin/events/${eventId}/publish/`,
        { is_public: isPublic }
      );
      return res.data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey }),
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/events/list/`);
      if (!res.data?.success)
        throw new Error("イベント一覧の取得に失敗しました");
      return res.data.data || [];
    },
    retry: 1,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const [keyword, setKeyword] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [publicFilter, setPublicFilter] = useState("all");

  const totalCount = (data || []).length;

  // 検索（イベント名・主催者名に含まれる）→ 公開の状態 で絞り込む
  const kw = normalize(keyword);
  const filtered = (data || []).filter(
    (ev) =>
      (!kw || normalize(ev.title).includes(kw) || normalize(ev.organizer).includes(kw)) &&
      (publicFilter === "all" || ev.public_status === publicFilter)
  );

  // 並びは、開催日の新しい順（同じ開催日はイベントIDの大きい順）。
  // 「開催」の絞り込みは、開催日が今日以降＝開催予定、前＝終了
  const today = todayStr();
  const newestFirst = (a, b) =>
    String(b.date || "").localeCompare(String(a.date || "")) || b.event_id - a.event_id;
  const sorted = [...filtered].sort(newestFirst);
  const events =
    dateFilter === "upcoming"
      ? sorted.filter((ev) => (ev.date || "") >= today)
      : dateFilter === "finished"
        ? sorted.filter((ev) => (ev.date || "") < today)
        : sorted;

  // 何か絞り込みが効いているか（0件のときの文言に使う）
  const isFiltering = !!kw || dateFilter !== "all" || publicFilter !== "all";

  return (
    <div className="eventListPage">
      <div className="eventListCard">
        <div className="eventListHeader">
          <div className="eventListTitle">イベント一覧</div>
        </div>

        <div className="eventListSearch">
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="イベント名・主催者で探す"
            aria-label="イベント名・主催者で探す"
          />
          <label className="eventListFilter">
            <span>開催</span>
            <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
              {DATE_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </label>
          <label className="eventListFilter">
            <span>公開</span>
            <select value={publicFilter} onChange={(e) => setPublicFilter(e.target.value)}>
              {PUBLIC_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </label>
        </div>

        {!isLoading && !isError && (
          <div className="eventListCount">
            {isFiltering ? `該当 ${events.length} 件 / 全 ${totalCount} 件` : `全 ${totalCount} 件`}
          </div>
        )}

        {isError && (
          <div className="eventListError">
            エラー: {error?.message || "イベント一覧の取得に失敗しました"}
          </div>
        )}

        {isLoading ? (
          <div className="eventListLoading">読み込み中...</div>
        ) : events.length === 0 ? (
          <div className="eventListEmpty">
            {isFiltering ? "該当するイベントがありません" : "イベントがありません"}
          </div>
        ) : (
          <div className="eventListBody">
            {events.map((ev) => {
              const statusDisplay = getApplyStatusDisplay(ev.apply_status);
              const publicDisplay = PUBLIC_STATUS[ev.public_status] || PUBLIC_STATUS.private;
              const isToggling =
                togglePublic.isPending && togglePublic.variables?.eventId === ev.event_id;
              const toggleFailed =
                togglePublic.isError && togglePublic.variables?.eventId === ev.event_id;
              return (
                <div
                  key={ev.event_id}
                  className={`eventListRow ${ev.is_entered ? "isEntered" : ""}`}
                >
                  <div className="eventListRowLeft">
                    <div className="eventListName">
                      {ev.title}{" "}
                      <span className="eventListMeta">【{ev.summary}】</span>

                      <span
                        className={`eventListStatusBadge ${statusDisplay.className}`}
                      >
                        {statusDisplay.label}
                      </span>

                      {ev.is_entered && (
                        <span className="eventListEnteredBadge">
                          エントリー済（{ev.entry_count}頭）
                        </span>
                      )}
                    </div>
                    <div className="eventListDate">
                      {formatDateJP(ev.date)}
                      {ev.organizer && `　主催：${ev.organizer}`}
                    </div>
                  </div>
                  <div className="eventListActions">
                  <div className="eventListPublish">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!ev.is_public}
                      aria-label="公開サイトに表示する"
                      className={`eventListSwitch ${ev.is_public ? "isOn" : ""}`}
                      disabled={isToggling}
                      title={ev.is_public ? "クリックで非公開にする" : "クリックで公開する"}
                      onClick={() =>
                        togglePublic.mutate({ eventId: ev.event_id, isPublic: !ev.is_public })
                      }
                    >
                      <span className="eventListSwitchThumb" aria-hidden="true" />
                    </button>
                    <span className="eventListPublishSlot">
                      <span className={`eventListPublishPill ${publicDisplay.className}`}>
                        <span className="eventListPublishDot" aria-hidden="true" />
                        <span className="eventListPublishText">
                          {isToggling ? "切り替え中..." : publicDisplay.label}
                          {!isToggling && publicDisplay.sub && (
                            <small className="eventListPublishSub">{publicDisplay.sub}</small>
                          )}
                        </span>
                      </span>
                    </span>
                    {toggleFailed && (
                      <span className="eventListPublishError">
                        {togglePublic.error?.response?.data?.message || "切り替えに失敗しました"}
                      </span>
                    )}
                  </div>
                  <div className="eventListSettingBox">
                    <span
                      className={`eventListSettingBadge ${ev.has_settings ? "isSet" : "isUnset"}`}
                    >
                      {ev.has_settings ? "設定済み" : "未設定"}
                    </span>
                    <Link
                      to={`/admin/events/${ev.event_id}/settings`}
                      className="eventListSettingsBtn"
                    >
                      設定
                    </Link>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
